import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { getBookingTime } from '@us/web/cart/util';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OrdersActions } from '@us/web/orders/data-access';
import { AppInit } from '@us/web/shared/app';
import {
    BusinessModel,
    DeliveryFee,
    Order,
    OrderDelivery,
    OrderItem,
    OrderState,
    OrderType,
    PaymentType,
    Shop,
    Table
} from '@us/web/shared/data-access';
import {
    calcTotalDiscount,
    calcTotalDuration,
    calcTotalItems,
    calcTotalPrice,
    getErrorMessage,
    uniqueId
} from '@us/web/shared/utils';
import { isBefore, parseISO } from 'date-fns';
import { cloneDeep, includes, isEqual, isNil } from 'lodash';
import { CartActions } from './cart.actions';

export const CART_FEATURE_KEY = 'cart';

export type Cart = Order;

export type CartItem = OrderItem;

export type CartFillable = Partial<
    Pick<
        Cart,
        | 'order_code'
        | 'payment_type'
        | 'discount'
        | 'surcharge'
        | 'tax'
        | 'grand_total'
        | 'paid_total'
        | 'duration_total'
        | 'note'
        | 'shop'
        | 'order_type'
        | 'order_table'
        | 'booking_time'
        | 'receiver'
        | 'delivery' // TODO: remove this
        | 'productList'
        | 'items'
    >
>;

export interface State {
    cart: Cart;
    deliveryFees: DeliveryFee[];
    loading: boolean;
    error: string | null;
}

export const initialState = (params: CartFillable = {}): State => ({
    cart: {
        order_id: uniqueId(),
        order_code: '',
        payment_type: PaymentType.Cash,
        grand_total: 0,
        paid_total: 0,
        note: '',
        items: [],
        duration_total: 0,
        current_state: OrderState.Pending,
        order_type: null, // OrderType.Delivery,
        total_price: 0,
        total_items: 0,
        version_code: 0,
        booking_time: getBookingTime(null), // OrderType.Delivery
        productList: [],
        ...params
    },
    deliveryFees: [],
    loading: false,
    error: null
});

export const refreshCart = (cart: Cart): Cart => {
    const totalPrice = calcTotalPrice(cart.items);
    const totalItems = calcTotalItems(cart.items);
    const durationTotal = calcTotalDuration(cart.items);
    let totalDiscount = calcTotalDiscount(cart.discount, totalPrice);
    let totalDiscountRank = calcTotalDiscount(cart.discount, totalPrice, true);
    if (cart.discount) {
        // reload discount when update cart
        cart.discount = { ...cart.discount, price: totalDiscount, price_rank: totalDiscountRank };
        const isInvalidMinBillValue = cart.discount.minBillValue && cart.discount.minBillValue > totalPrice;
        if (totalItems === 0 || isInvalidMinBillValue) {
            cart.discount = { ...cart.discount, price: 0, percent: 0, price_rank: 0 };
            totalDiscount = 0;
            totalDiscountRank = 0;
        }
    }
    const grandTotal =
        totalPrice -
        (totalDiscount + totalDiscountRank) -
        (cart.tax?.price || 0) +
        (cart.surcharge?.price || 0) +
        (cart.delivery?.delivery_fee || 0);

    const bookingTime =
        cart.version_code === 0 && isBefore(parseISO(cart.booking_time), new Date())
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              getBookingTime(cart.order_type!)
            : cart.booking_time;

    return {
        ...cart,
        total_price: totalPrice,
        total_items: totalItems,
        duration_total: durationTotal,
        grand_total: grandTotal,
        ...(cart.version_code === 0 ? { booking_time: bookingTime } : {})
    };
};

const addToCart = (state: State, item: CartItem, shop?: Shop, orderType?: OrderType | null, table?: Table): State => {
    const cart: Cart = cloneDeep(state.cart);
    if (cart.shop && cart.shop.id !== shop?.id) {
        console.error('Shop mismatch');
        return state;
    }

    const found = cart.items.findIndex(
        e =>
            e.version_code === 0 &&
            (item.uuid === undefined
                ? e.product_id === item.product_id && e.unit.id === item.unit.id && isEqual(e.options, item.options)
                : e.uuid === item.uuid)
    );

    if (found === -1) {
        cart.items.push({
            ...item,
            uuid: item.uuid || uniqueId()
        });
    } else {
        if (cart.items[found].quantity + item.quantity <= 0) {
            cart.items.splice(found, 1);
        } else {
            cart.items[found] = {
                ...cart.items[found],
                ...item,
                uuid: cart.items[found].uuid || item.uuid || uniqueId(),
                quantity: cart.items[found].quantity + item.quantity
            };
        }
    }

    if (!isNil(orderType)) {
        cart.order_type = orderType;
    }

    if (!isNil(table)) {
        cart.order_table = cloneDeep(table);
    }

    cart.delivery = null;
    cart.shop = cloneDeep(shop);
    return { ...state, cart: refreshCart(cart) };
};

const removeFromCart = (state: State, item: CartItem | { uuid: string }): State => {
    const cart: Cart = cloneDeep(state.cart);
    if (!item.uuid) {
        console.error('Fix me!!!');
    }
    cart.items = cart.items.filter(e => e.uuid !== item.uuid);
    cart.delivery = null;
    return { ...state, cart: refreshCart(cart) };
};

const updateItemQuantity = (state: State, item: CartItem, quantity: number): State => {
    if (quantity <= 0) {
        return removeFromCart(state, item);
    }

    const cart: Cart = cloneDeep(state.cart);
    if (!item.uuid) {
        console.error('Fix me!!!');
    }
    const index = cart.items.findIndex(e => e.uuid === item.uuid);

    if (index === -1) {
        return state;
    }

    cart.items[index].quantity = quantity;
    cart.delivery = null;
    return { ...state, cart: refreshCart(cart) };
};

const changeDeliveryService = (state: State, delivery: OrderDelivery): State => {
    const cart: Cart = cloneDeep(state.cart);
    cart.delivery = delivery;
    return { ...state, cart: refreshCart(cart) };
};

const updateCart = (state: State, value: CartFillable): State => {
    if (
        !isNil(value.order_table) &&
        (isNil(state.cart.order_table) || value.order_table.id !== state.cart.order_table.id)
    ) {
        const newState = initialState();
        return {
            ...newState,
            cart: {
                ...newState.cart,
                ...value,
                shop: state.cart.shop
            }
        };
    }

    const cart = {
        ...state.cart,
        ...value
    } as Cart;
    if ('surcharge' in value || 'tax' in value || 'receiver' in value || 'order_type' in value) {
        cart.delivery = null;
    }

    if ('order_type' in value && value.order_type !== OrderType.OrderAtTheTable) {
        cart.order_table = undefined;
    }

    return { ...state, cart: refreshCart(cart) };
};

export const cartFeature = createFeature({
    name: CART_FEATURE_KEY,
    reducer: createReducer(
        initialState(),
        on(CartActions.addToCartSuccess, (state: State, { item, shop, orderType, table }) =>
            addToCart(state, item, shop, orderType, table)
        ),
        on(CartActions.removeFromCart, (state: State, { item }: { item: CartItem | { uuid: string } }) =>
            removeFromCart(state, item)
        ),
        on(CartActions.updateItemQuantity, (state: State, { item, quantity }) =>
            updateItemQuantity(state, item, quantity)
        ),
        on(CartActions.updateCart, (state: State, value: CartFillable) => updateCart(state, value)),
        on(CartActions.changeDeliveryService, (state: State, delivery: OrderDelivery) =>
            changeDeliveryService(state, delivery)
        ),
        on(CartActions.selectTableSuccess, (state: State, { table }) =>
            updateCart(state, {
                order_table: table,
                order_type: OrderType.OrderAtTheTable
            })
        ),
        on(CartActions.clearCart, (state, { item, shop }) => {
            const newState = initialState({
                shop,
                order_type: state.cart.order_type
            });
            return item ? addToCart(newState, item, shop) : newState;
        }),
        on(
            CartActions.submitCart,
            (state: State, { businessModel }): State => ({
                ...state,
                loading: true
            })
        ),
        on(
            CartActions.submitCartFailed,
            (state: State, { error }: any): State => ({
                ...state,
                loading: false,
                error: getErrorMessage(error, 'error')
            })
        ),
        on(CartActions.submitCartSuccess, (state: State, { cart }) => {
            if (state.cart.order_type !== OrderType.TakeAway && state.cart.order_type !== OrderType.OrderAtTheTable) {
                return initialState();
            }
            return {
                ...state,
                cart,
                loading: false
            };
        }),
        on(CartActions.pushOrderState, (state: State, { new_state }): State => {
            return {
                ...state,
                cart: {
                    ...state.cart,
                    current_state: new_state
                },
                loading: true
            };
        }),
        on(
            CartActions.pushOrderStateSuccess,
            (state: State, { order }): State => ({
                ...state,
                cart: order,
                loading: false
            })
        ),
        on(
            CartActions.pushOrderStateFailed,
            (state: State, { error }): State => ({
                ...state,
                loading: false,
                error
            })
        ),
        on(OrdersActions.loadOrder, OrdersActions.loadOrderPaymentStatus, (state: State, { orderId }): State => {
            if (orderId !== state.cart.order_id) {
                return state;
            }

            return {
                ...state,
                loading: true
            };
        }),
        on(OrdersActions.loadOrderSuccess, (state: State, { order }: any): State => {
            if (order.order_id !== state.cart.order_id) {
                return state;
            }

            const businessModels =
                (Array.isArray(order.shop?.business_model)
                    ? order.shop?.business_model
                    : [order.shop?.business_model]) || [];

            if (
                order.order_type !== OrderType.OrderAtTheTable ||
                businessModels.indexOf(BusinessModel.PrePayment) !== -1 ||
                includes([OrderState.Canceled, OrderState.Rejected, OrderState.Complete], order.current_state)
            ) {
                return initialState({ order_type: order.order_type });
            }
            return {
                ...state,
                cart: order,
                loading: false
            };
        }),
        on(OrdersActions.loadOrderFailed, (state: State, { error, orderId }: any): State => {
            if (orderId !== state.cart.order_id) {
                return state;
            }
            return {
                ...state,
                loading: false,
                error
            };
        }),
        on(CartActions.resetCartState, (state: State): State => ({ ...state, loading: false })),
        on(AppInit, (state: State): State => ({ ...state, loading: false })),
        on(CartActions.updateCartWithPromotion, (state: State, value: CartFillable) => updateCart(state, value))
        // on(
        //     CartActions.selectShopSuccess,
        //     (state: State, { shop }): State => ({
        //         ...state,
        //         cart: {
        //             ...state.cart,
        //             shop
        //         }
        //     })
        // )
    )
});

export const { name, reducer, selectCartState, selectCart, selectLoading, selectError } = cartFeature;

export const selectOrderId = createSelector(selectCart, (cart: Cart) => cart?.order_id);

export const selectCartItems = createSelector(selectCart, (cart: Cart) => cart?.items);

export const selectCartTotalItems = createSelector(selectCart, (cart: Cart) => cart?.total_items || 0);

export const selectCartTotalNewItems = createSelector(selectCartItems, items =>
    items.reduce((total, item) => total + (item.version_code === 0 ? Number(item?.quantity) : 0), 0)
);

export const selectOrderType = createSelector(selectCart, (cart: Cart) => cart?.order_type || null);

export const selectCartTable = createSelector(selectCart, (cart: Cart) => cart?.order_table);

export const selectCartTableId = createSelector(selectCartTable, (table: Table | undefined) => table?.id);

export const selectCartPaymentType = createSelector(selectCart, (cart: Cart) => cart?.payment_type);

export const selectCartTotal = createSelector(selectCart, (cart: Cart) => cart?.grand_total || 0);
export const selectCartTotalPrice = createSelector(selectCart, (cart: Cart) => cart?.total_price || 0);

export const selectItemQuantityInCart = (productId: string) =>
    createSelector(
        selectCartItems,
        items =>
            items?.reduce(
                (t, item) => t + (item.product_id === productId ? item.quantity * (item.unit?.ratio || 1) : 0),
                0
            ) || 0
    );

export const selectShowAddItemButton = createSelector(
    selectCart,
    (cart: Cart) =>
        !cart.paid_total &&
        ![OrderState.Complete, OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(cart.current_state)
);

export const selectIsNewOrder = createSelector(selectCart, (cart: Cart) => cart?.version_code === 0);

export const selectSelectedItemQuantityInCart = (productId: string) =>
    createSelector(
        selectCartItems,
        items =>
            items?.reduce(
                (t, item) =>
                    t +
                    (item.version_code === 0 && item.product_id === productId
                        ? item.quantity * (item.unit?.ratio || 1)
                        : 0),
                0
            ) || 0
    );

export const selectItemInCart = (productId: string) =>
    createSelector(
        selectCartItems,
        items =>
            items?.reduce((t, item) => {
                if (item.product_id !== productId || item.version_code > 0) {
                    return t;
                }
                t.push(item);

                return t;
            }, [] as Array<CartItem>) || []
    );

export const selectCartShop = createSelector(selectCart, (cart: Cart) => cart?.shop);

export const selectCartReceiver = createSelector(selectCart, (cart: Cart) => cart?.receiver || null);

export const selectIsNewCart = createSelector(selectCart, (cart: Cart) => !cart?.version_code);

export const selectCartBookingTime = createSelector(selectCart, (cart: Cart) =>
    cart?.booking_time ? parseISO(cart.booking_time) : null
);
